
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { ProfileSidebar } from "@/components/profile/ProfileSidebar";
import { useProfileCompleteness } from "@/hooks/useProfileCompleteness";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileCardHeader } from "@/components/profile/ProfileCardHeader";
import { ProfileViewMode } from "@/components/profile/ProfileViewMode";
import { ProfileEditForm } from "@/components/profile/ProfileEditForm";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { AddressData } from "@/components/ui/AddressMapInput";

interface ProfileType {
  id: string;
  display_name: string | null;
  email: string | null;
  updated_at: string | null;
  address: string | null;
  photo_url: string | null;
  about_me: string | null;
  location: string | null;
}

interface ProfileFormValues {
  displayName: string;
  phone: string;
  address: string;
  location: string;
  addressData: AddressData;
  emailBookingNotifications: boolean;
  smsBookingNotifications: boolean;
}

const MyProfile = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileData, setProfileData] = useState<ProfileType | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isEditModeFromRedirect, setIsEditModeFromRedirect] = useState(false);
  const [redirectReason, setRedirectReason] = useState<string | null>(null);
  const { isComplete, isLoading, updateCompletionStatus } = useProfileCompleteness();
  const { preferences, savePreferences } = useNotificationPreferences();
  
  const form = useForm<ProfileFormValues>({
    defaultValues: {
      displayName: "",
      phone: "",
      address: "",
      location: "",
      addressData: { address: "", mapLink: "", directions: "" },
      emailBookingNotifications: true,
      smsBookingNotifications: true
    },
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setInitialLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Auth session check:", session ? "Session exists" : "No session");
        
        if (!session) {
          console.log("No session, redirecting to auth");
          navigate('/auth');
          return;
        }
        
        setUser(session.user);
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();
          
        if (error) {
          console.error("Error fetching profile:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Could not fetch your profile",
          });
          return;
        }
        
        console.log("Profile fetch result:", profile);
            
        if (profile) {
          const typedProfile = profile as ProfileType;
          setProfileData(typedProfile);

          form.setValue('displayName', typedProfile.display_name || '');
          form.setValue('address', typedProfile.address || '');
          form.setValue('location', typedProfile.location || '');
          
          // Initialize addressData with legacy data
          form.setValue('addressData', {
            address: typedProfile.address || '',
            mapLink: typedProfile.location || '',
            directions: ''
          });
        }
      } catch (error: any) {
        console.error("Error in fetchProfile:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "An error occurred",
        });
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchProfile();
  }, [navigate, form, toast]);

  // Load notification preferences into form when available
  useEffect(() => {
    if (preferences) {
      form.setValue('phone', preferences.notification_phone || '');
      form.setValue('emailBookingNotifications', preferences.email_booking_updates);
      form.setValue('smsBookingNotifications', preferences.sms_booking_updates);
    }
  }, [preferences, form]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const editParam = queryParams.get('edit');
    const reasonParam = queryParams.get('reason');
    
    if (editParam === 'true') {
      setIsEditMode(true);
      setIsEditModeFromRedirect(true);
    }
    
    if (reasonParam) {
      setRedirectReason(reasonParam);
    }
  }, []);

  useEffect(() => {
    if (isEditModeFromRedirect && redirectReason === 'booking' && !isLoading && !isComplete) {
      toast({
        title: "Complete Your Profile",
        description: "You need to provide your phone number and address to book services. Please fill in these required fields.",
        duration: 5000,
      });
    }
    
    // Clean up URL parameters after processing to prevent repeated messages
    if (isEditModeFromRedirect || redirectReason) {
      const url = new URL(window.location.href);
      url.searchParams.delete('edit');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
  }, [isEditModeFromRedirect, redirectReason, isLoading, isComplete, toast]);

  const onSubmit = async (data: ProfileFormValues) => {
    if (!user) {
      console.error("Cannot update profile: User is not defined");
      return;
    }
    
    try {
      setLoading(true);
      console.log("Submitting profile update:", data);
      
      // Update profiles table using addressData 
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          display_name: data.displayName,
          address: data.addressData.address,
          location: data.addressData.mapLink || data.location,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) {
        console.error("Error updating profile:", profileError);
        throw profileError;
      }

      // Update notification preferences (phone and notification settings)
      if (preferences) {
        await savePreferences({
          ...preferences,
          notification_phone: data.phone,
          email_booking_updates: data.emailBookingNotifications,
          email_new_bookings: data.emailBookingNotifications,
          email_reviews: data.emailBookingNotifications,
          sms_booking_updates: data.smsBookingNotifications,
          sms_new_bookings: data.smsBookingNotifications,
          sms_reviews: data.smsBookingNotifications,
        });
      }

      console.log("Profile updated successfully");
      
      // Update completion status after profile changes
      await updateCompletionStatus();
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
      
      if (profileData) {
        setProfileData({
          ...profileData,
          display_name: data.displayName,
          address: data.addressData.address,
          location: data.addressData.mapLink || data.location,
          updated_at: new Date().toISOString()
        });
      }
      
      if (isEditModeFromRedirect && redirectReason === 'booking') {
        setTimeout(() => {
          handleRedirectBack();
        }, 1000);
      } else {
        setIsEditMode(false);
      }
      
    } catch (error: any) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred while updating your profile",
      });
    } finally {
      setLoading(false);
    }
  };

  const enterEditMode = () => {
    setIsEditMode(true);
  };

  const cancelEdit = () => {
    if (profileData) {
      form.setValue('displayName', profileData.display_name || '');
      form.setValue('address', profileData.address || '');
      form.setValue('location', profileData.location || '');
      
      // Reset addressData with legacy data
      form.setValue('addressData', {
        address: profileData.address || '',
        mapLink: profileData.location || '',
        directions: ''
      });
    }
    
    if (preferences) {
      form.setValue('phone', preferences.notification_phone || '');
      form.setValue('emailBookingNotifications', preferences.email_booking_updates);
      form.setValue('smsBookingNotifications', preferences.sms_booking_updates);
    }
    
    setIsEditMode(false);
  };

  const handleRedirectBack = () => {
    const originalPath = sessionStorage.getItem("originalPath");
    if (originalPath) {
      navigate(originalPath);
      sessionStorage.removeItem("originalPath");
    } else {
      navigate("/");
    }
  };

  const renderRedirectBackButton = () => {
    if (isEditModeFromRedirect && redirectReason === 'booking') {
      return (
        <Button
          onClick={handleRedirectBack}
          variant="outline"
          className="mt-4"
        >
          Return to Service
        </Button>
      );
    }
    return null;
  };

  const showTosWelcomeMessage = isEditModeFromRedirect && redirectReason === 'tos';

  useEffect(() => {
    if (isEditModeFromRedirect && redirectReason === 'tos' && !initialLoading) {
      const timer = setTimeout(() => {
        const phoneInput = document.querySelector('input[name="phone"]');
        if (phoneInput instanceof HTMLElement) {
          phoneInput.focus();
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isEditModeFromRedirect, redirectReason, initialLoading]);

  if (initialLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header showSearchAndLocation={false} />
        <div className="container pt-24 flex-grow flex justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-500">Loading your profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        {/* Fixed header */}
        <div className="fixed top-0 left-0 right-0 z-40 h-16">
          <Header showSearchAndLocation={false} />
        </div>
        
        {/* Main layout with sidebar - starts after header */}
        <div className="flex flex-1 pt-16">
          <ProfileSidebar />
          
          <SidebarInset>
            {/* Main content with proper spacing to clear header */}
            <main className="flex-1 overflow-auto p-4 md:p-6">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-4">My Profile</h1>
                
                {showTosWelcomeMessage && (
                  <div className="mb-6 p-4 border-l-4 border-green-500 bg-green-50 rounded-r-md">
                    <h3 className="font-medium text-green-800">Welcome to LamuLamu!</h3>
                    <p className="text-green-700 text-sm mt-1">
                      Let's finish setting up your profile with your contact information.
                      This will help us serve you better and enable you to book services.
                    </p>
                  </div>
                )}
                
                <ProfileHeader 
                  isComplete={isComplete} 
                  isEditMode={isEditMode} 
                  redirectReason={redirectReason}
                />
              
                <Card className="w-full">
                  <ProfileCardHeader 
                    isEditMode={isEditMode}
                    loading={loading}
                    cancelEdit={cancelEdit}
                    enterEditMode={enterEditMode}
                    handleSubmit={form.handleSubmit(onSubmit)}
                  />
                  <CardContent>
                    {isEditMode ? (
                      <Form {...form}>
                        <form className="space-y-6">
                          <ProfileEditForm 
                            form={form}
                            loading={loading}
                            cancelEdit={cancelEdit}
                            handleSubmit={form.handleSubmit(onSubmit)}
                          />
                        </form>
                      </Form>
                    ) : (
                      <ProfileViewMode 
                        profileData={profileData}
                      />
                    )}
                  </CardContent>
                </Card>
                
                {renderRedirectBackButton()}
              </div>
            </main>
          </SidebarInset>
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
    </SidebarProvider>
  );
};

export default MyProfile;
